// src/pages/Advertisement/CalendarModal.tsx
import React, { useEffect, useMemo, useState } from "react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  value: Date | null;
  onSelect: (date: Date) => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
  minDate?: Date | null;
};

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// Monday-first index: Mon=0 ... Sun=6
function weekdayIndexMonFirst(d: Date) {
  const js = d.getDay(); // Sun=0..Sat=6
  return (js + 6) % 7; // Mon=0..Sun=6
}

function addMonths(d: Date, delta: number) {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

function addYears(d: Date, delta: number) {
  return new Date(d.getFullYear() + delta, d.getMonth(), 1);
}

export default function CalendarModal({
  open,
  onOpenChange,
  title = "Select date",
  value,
  onSelect,
  anchorRef,
  minDate,
}: Props) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const minSelectable = useMemo(() => {
    if (!minDate) return today;
    const md = startOfDay(minDate);
    return md.getTime() > today.getTime() ? md : today;
  }, [minDate, today]);
  
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

useEffect(() => {
  if (!open) return;

  const el = anchorRef?.current;
  if (!el) {
    setPos(null);
    return;
  }

  const rect = el.getBoundingClientRect();

  // popover size (matches your modalStyle constraints)
  const W = 380;
  const H = 340;
  const GAP = 8;

  // Above input by default
  let top = rect.top - H - GAP;
  // If not enough space above, show below
  if (top < 8) top = rect.bottom + GAP;

  // Align left with the input (clamp to viewport)
  let left = rect.left-21;
  left = Math.max(8, Math.min(left, window.innerWidth - W - 8));

  setPos({ top, left });
}, [open, anchorRef]);


useEffect(() => {
  if (!open) return;

  const onDown = (e: MouseEvent) => {
    const target = e.target as Node | null;
    const el = anchorRef?.current;

    // if clicking the input again, ignore (your input opens it)
    if (el && target && el.contains(target)) return;

    // close when clicking outside the calendar
    const cal = document.getElementById("calendar-popover");
    if (cal && target && cal.contains(target)) return;

    onOpenChange(false);
  };

  window.addEventListener("mousedown", onDown);
  return () => window.removeEventListener("mousedown", onDown);
}, [open, anchorRef, onOpenChange]);

const isAnchored = !!anchorRef?.current && !!pos;

  const [hoverKey, setHoverKey] = useState<string | null>(null);

const BORDER_DEFAULT = "0.5px solid #444";
const BORDER_HOVER = "2px solid rgb(189, 156, 47)";


  const [viewMonth, setViewMonth] = useState<Date>(() => {
    const base = value ? startOfDay(value) : today;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });


  // When opening, sync the viewed month to the selected value (or today)
  useEffect(() => {
    if (!open) return;
    const base = value ? startOfDay(value) : today;
    setViewMonth(new Date(base.getFullYear(), base.getMonth(), 1));
  }, [open, value, today]);

  const monthLabel = useMemo(() => {
    // Using built-in Intl for a nice label
    try {
      return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(viewMonth);
    } catch {
      return `${viewMonth.getFullYear()}-${String(viewMonth.getMonth() + 1).padStart(2, "0")}`;
    }
  }, [viewMonth]);

  const daysGrid = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();

    const firstOfMonth = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const firstWeekday = weekdayIndexMonFirst(firstOfMonth); // 0..6 (Mon-first)

    const cells: Array<{ date: Date | null; dayNumber: number | null }> = [];

    // Leading blanks
    for (let i = 0; i < firstWeekday; i++) {
      cells.push({ date: null, dayNumber: null });
    }

    // Month days
    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(year, month, day);
      cells.push({ date: d, dayNumber: day });
    }

    // Trailing blanks to complete rows (up to 6 rows max)
    while (cells.length % 7 !== 0) {
      cells.push({ date: null, dayNumber: null });
    }
    // Ensure consistent height: 6 rows
    while (cells.length < 42) {
      cells.push({ date: null, dayNumber: null });
    }

    return cells;
  }, [viewMonth]);

  if (!open) return null;

  const overlayStyle: React.CSSProperties = isAnchored
  ? {
      position: "fixed",
      inset: 0,
      background: "transparent",
      zIndex: 9999,
    }
  : {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
      zIndex: 9999,
    };

    const modalStyle: React.CSSProperties = isAnchored
    ? {
        position: "fixed",
        top: pos!.top,
        left: pos!.left,
        width: 380,
        height: 340,
        background: "#020617",
        border: "1.5px solid #ccc",
        borderRadius: 14,
        boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
        overflow: "hidden",
      }
    : {
        width: "min(380px, 72vw)",
        height: "min(340px, 57vw)",
        background: "#020617",
        border: "2px solid #000",
        borderRadius: 14,
        boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
        overflow: "hidden",
      };

  const headerStyle: React.CSSProperties = {
    padding: "14px 16px 10px",
    borderBottom: "1px solid rgba(0,0,0,0.12)",
  };

  const titleStyle: React.CSSProperties = {
    marginTop: -1,
    fontSize: 14,
    fontWeight: 700,
    color: "#ccc",
    textAlign: "center",
  };
  

  const navRowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "12px 12px 8px",
    marginTop: -25
  };

  const arrowBtnStyle: React.CSSProperties = {
    width: 26,
    height: 26,
    borderRadius: 5,
    border: BORDER_DEFAULT,
    background: "transparent",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none",
    transition: "border-color 120ms ease",
  };
  const arrowIconStyle: React.CSSProperties = {
    fontSize: 18,
    lineHeight: 1,
    color: "#ccc",
    fontWeight: 800,
  };

  const monthLabelWrapStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flex: 1,
    justifyContent: "center",
    minWidth: 0,
  };

  const monthTextStyle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 700,
    color: "#ccc",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const weekHeaderStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    padding: "8px 12px 6px",
    gap: 6,
  };

  const weekDayStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 700,
    color: "#ccc",
    textAlign: "center",
    userSelect: "none",
  };

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    padding: "0 12px 14px",
    gap: 6,
  };

  const dayCellBase: React.CSSProperties = {
    height: 34,
    width: 35,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    border: "1px solid rgba(0,0,0,0.10)",
    userSelect: "none",
  };

  const blankCellStyle: React.CSSProperties = {
    height: 40,
    borderRadius: 10,
    // no border, no background, nothing clickable
    border: "1px solid transparent",
    background: "transparent",
  };

  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div
  style={overlayStyle}
  onMouseDown={() => {
    // only close on overlay click in centered modal mode
    if (!isAnchored) onOpenChange(false);
  }}
  role="dialog"
  aria-modal="true"
>
<div
  id="calendar-popover"
  style={modalStyle}
  onMouseDown={(e) => e.stopPropagation()}
>
          <div style={headerStyle}>
          <p style={titleStyle}>{title}</p>
        </div>

        {/* Month nav (prev on left, next on right) + optional year arrows */}
        <div style={navRowStyle}>
     

          <div style={monthLabelWrapStyle}>
          <button
  type="button"
  onClick={() => setViewMonth((m) => addMonths(m, -1))}
  onMouseEnter={() => setHoverKey("prev")}
  onMouseLeave={() => setHoverKey(null)}
  style={{
    ...arrowBtnStyle,
    border: hoverKey === "prev" ? BORDER_HOVER : BORDER_DEFAULT,
  }}
  aria-label="Previous month"
>
  <span style={arrowIconStyle}>‹</span>
</button>

            <div style={monthTextStyle} aria-label="Current month">
              {monthLabel}
            </div>

            <button
  type="button"
  onClick={() => setViewMonth((m) => addMonths(m, 1))}
  onMouseEnter={() => setHoverKey("next")}
  onMouseLeave={() => setHoverKey(null)}
  style={{
    ...arrowBtnStyle,
    border: hoverKey === "next" ? BORDER_HOVER : BORDER_DEFAULT,
  }}
  aria-label="Next month"
>
  <span style={arrowIconStyle}>›</span>
</button>
          </div>      
        </div>

        {/* Weekday header */}
        <div style={weekHeaderStyle}>
          {weekDays.map((wd) => (
            <div key={wd} style={weekDayStyle}>
              {wd}
            </div>
          ))}
        </div>

        {/* Days grid */}
        <div style={gridStyle}>
          {daysGrid.map((cell, idx) => {
            if (!cell.date || !cell.dayNumber) {
              // No “field” when there is no number (just an invisible spacer)
              return <div key={`blank-${idx}`} style={blankCellStyle} />;
            }

            const cellDate = startOfDay(cell.date);
            const isPast = cellDate.getTime() < today.getTime();
            const isBeforeMin = cellDate.getTime() < minSelectable.getTime();
            const disabled = isPast || isBeforeMin;
            
            const clickable = !disabled;            
            const isToday = isSameDay(cellDate, today);
            const isSelected = value ? isSameDay(startOfDay(value), cellDate) : false;
            const cellKey = `day-${cellDate.getFullYear()}-${cellDate.getMonth()}-${cellDate.getDate()}`;
            const isHover = clickable && hoverKey === cellKey;
            
            const base: React.CSSProperties = {
              ...dayCellBase,
              cursor: clickable ? "pointer" : "default",
              opacity: disabled ? 0.5 : 1,
              pointerEvents: clickable ? "auto" : "none",
              background: isSelected ? "#222" : "#bbb",
              color: isSelected ? "#ccc" : "#111",
              border: isHover
  ? BORDER_HOVER
  : isToday
  ? "1px solid #000"
  : isSelected
  ? "1px solid #000"
  : "1px solid rgba(0,0,0,0.10)",
transition: "border-color 120ms ease",
};

            return (
              <div
                key={`${cellDate.toISOString()}-${idx}`}
                style={base}
                onMouseEnter={() => clickable && setHoverKey(cellKey)}
                onMouseLeave={() => setHoverKey(null)}
                onClick={() => {
                  onSelect(cellDate);
                  onOpenChange(false);
                }}
                aria-label={`Day ${cell.dayNumber}`}
              >
                {cell.dayNumber}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
